<template>
  <v-row>
    <!-- Campo de teléfono -->
    <v-col cols="12" md="4">
      <v-text-field
        label="Número de teléfono"
        v-model="phoneNumber"
        placeholder="Ingresa el número de teléfono"
        clearable
      />
    </v-col>

    <!-- Selector de fecha "Desde" -->
    <v-col cols="12" md="4">
      <v-menu
        v-model="menuFromDate"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formatDateTime(fromDate, fromHour, fromMinute)"
            label="Fecha desde"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-card>
          <v-date-picker
            v-model="fromDate"
            no-title
            scrollable
            :max="maxFromDate"
            :min="minFromDate"
            @input="onFromDateChange"
            color="#006cb7"
          />
          <v-row class="px-4 pt-2">
            <v-col cols="12">
              <v-select
                v-model="fromHour"
                :items="hours"
                label="Hora"
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <v-row class="px-4 pt-2">
            <v-col cols="12">
              <v-select
                v-model="fromMinute"
                :items="minutes"
                label="Minuto"
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <v-card-actions class="d-flex justify-end">
            <v-btn text @click="menuFromDate = false">Aceptar</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-col>

    <!-- Selector de fecha "Hasta" -->
    <v-col cols="12" md="4">
      <v-menu
        v-model="menuToDate"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formatDateTime(toDate, toHour, toMinute)"
            label="Fecha hasta"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
            :disabled="!fromDate"
          />
        </template>
        <v-card>
          <v-date-picker
            v-model="toDate"
            no-title
            scrollable
            :min="minToDate || fromDate"
            :max="maxToDate"
            color="#006cb7"
          />
          <v-row class="px-4 pt-2">
            <v-col cols="12">
              <v-select
                v-model="toHour"
                :items="hours"
                label="Hora"
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <v-row class="px-4 pt-2">
            <v-col cols="12">
              <v-select
                v-model="toMinute"
                :items="minutes"
                label="Minuto"
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <v-card-actions class="d-flex justify-end">
            <v-btn text @click="menuToDate = false">Aceptar</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-col>

    <!-- Botón de aplicar filtros -->
    <v-col cols="12">
      <v-btn color="primary" @click="apply" :disabled="isApplyButtonDisabled">Aplicar</v-btn>
    </v-col>
  </v-row>
</template>

<script>
export default {
  data() {
    return {
      phoneNumber: '',
      menuFromDate: false,
      menuToDate: false,
      fromDate: null,
      toDate: null,
      fromHour: '00',
      fromMinute: '00',
      toHour: '23',
      toMinute: '59',
      maxFromDate: this.calculateMaxFromDate(),
      minFromDate: null, // Sin límite mínimo
      maxToDate: this.calculateMaxToDate(),
      minToDate: null,
      hours: Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')),
      minutes: Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')),
    };
  },
  computed: {
    isApplyButtonDisabled() {
      // Si no hay número de teléfono, solo requiere fechas válidas
      if (!this.phoneNumber) {
        return !(this.fromDate && this.toDate);
      }
      // Si hay número de teléfono, debe cumplir con la lógica completa
      return !(this.phoneNumber && this.fromDate && this.toDate);
    },
  },
  watch: {
    phoneNumber(newPhone) {
      if (!newPhone) {
        // Ajustar automáticamente el rango de fechas cuando no hay teléfono
        this.setDefaultDateRangeForNoPhoneNumber();
      } else {
        // Restaurar límites de fecha normales cuando hay un número de teléfono
        this.maxToDate = this.calculateOneMonthLater(this.fromDate);
      }
    },
  },
  methods: {
    calculateMaxFromDate() {
      const currentDate = new Date();
      return currentDate.toISOString().split('T')[0];
    },
    calculateMaxToDate() {
      const currentDate = new Date();
      return currentDate.toISOString().split('T')[0];
    },
    calculateOneMonthLater(date) {
      const selectedDate = new Date(date);
      const oneMonthLater = new Date(selectedDate.setMonth(selectedDate.getMonth() + 1));
      const currentDate = new Date();
      return oneMonthLater > currentDate ? currentDate.toISOString().split('T')[0] : oneMonthLater.toISOString().split('T')[0];
    },
    setDefaultDateRangeForNoPhoneNumber() {
      const currentDate = new Date();
      const oneWeekAgo = new Date(currentDate);
      oneWeekAgo.setDate(currentDate.getDate() - 7);

      this.fromDate = oneWeekAgo.toISOString().split('T')[0];
      this.toDate = currentDate.toISOString().split('T')[0];
      this.maxToDate = this.toDate;
    },
    onFromDateChange() {
      if (!this.phoneNumber) {
        // Ajustar automáticamente para mantener un rango de una semana
        const selectedDate = new Date(this.fromDate);
        const oneWeekLater = new Date(selectedDate);
        oneWeekLater.setDate(selectedDate.getDate() + 7);

        const currentDate = new Date();
        this.toDate = oneWeekLater > currentDate ? currentDate.toISOString().split('T')[0] : oneWeekLater.toISOString().split('T')[0];
        this.maxToDate = this.toDate;
      } else {
        this.minToDate = this.fromDate;
        this.maxToDate = this.calculateOneMonthLater(this.fromDate);
      }
    },
    formatDateTime(date, hour, minute) {
      if (!date) return '';
      const h = hour || '00';
      const m = minute || '00';
      return `${date} ${h}:${m}`;
    },
    apply() {
      const normalizedPhoneNumber = this.phoneNumber.replace(/\+/g, '');
      const fromDateTime = this.formatDateTime(this.fromDate, this.fromHour, this.fromMinute);
      const toDateTime = this.formatDateTime(this.toDate, this.toHour, this.toMinute);

      this.$emit('apply-filters', {
        phoneNumber: normalizedPhoneNumber,
        fromDate: fromDateTime,
        toDate: toDateTime,
      });
    },
  },
};
</script>


